import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authService, referralService, claimPendingReferral, REFERRAL_CODE_KEY } from '../services/api';
import type { ReferralInfo } from '../types';
import { useSubscription } from '../hooks/useSubscription';
import AvatarUploader from '../components/AvatarUploader';
import toast from 'react-hot-toast';
import { User, Lock, Edit2, Save, X, KeyRound, Gift, Copy, Check, Crown, UserCheck, UserPlus, Camera } from 'lucide-react';

const statusChip = (status: string) => {  switch (status) {
    case 'PAID':
      return (
        <span className="inline-flex items-center px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-xs font-medium">
          Paid
        </span>
      );
    case 'VERIFIED':
      return (
        <span className="inline-flex items-center px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">
          Verified
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center px-2 py-0.5 bg-zinc-100 text-zinc-500 rounded-full text-xs font-medium">
          Pending
        </span>
      );
  }
};

const Profile: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { isPro, loading: subLoading } = useSubscription();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [referral, setReferral] = useState<ReferralInfo | null>(null);
  const [referralLoading, setReferralLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [claimCode, setClaimCode] = useState('');
  const [claiming, setClaiming] = useState(false);
  const [showClaimForm, setShowClaimForm] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);

  const hasPassword = user?.hasPassword ?? false;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await claimPendingReferral();
      } catch {
        // Non-blocking; info below reflects whatever is recorded.
      }
      try {
        const res = await referralService.getInfo();
        if (!cancelled) setReferral(res.data);
      } catch {
        if (!cancelled) setReferral(null);
      } finally {
        if (!cancelled) setReferralLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const referralLink = referral?.code ? `${window.location.origin}/?ref=${referral.code}` : null;

  const handleCopy = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link.');
    }
  };

  const progressPct = referral
    ? Math.min(100, Math.round((referral.verifiedCount / referral.threshold) * 100))
    : 0;
  const isDone = referral ? (referral.paidCount >= 1 || referral.verifiedCount >= referral.threshold) : false;

  const handleClaimReferral = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = claimCode.trim();
    if (!code) {
      toast.error('Please enter a referral code.');
      return;
    }
    setClaiming(true);
    try {
      await referralService.claim(code);
      localStorage.removeItem(REFERRAL_CODE_KEY);
      setClaimCode('');
      setShowClaimForm(false);
      toast.success('Referral code applied!');
      const res = await referralService.getInfo();
      setReferral(res.data);
    } catch (error) {
      const err = error as { response?: { status?: number; data?: { error?: string } } };
      if (err.response?.status === 400) {
        toast.error(err.response?.data?.error || 'Invalid referral code.');
      } else {
        toast.error('Failed to apply referral code. Please try again.');
      }
    } finally {
      setClaiming(false);
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profileData.firstName.trim()) {
      toast.error('First name is required.');
      return;
    }
    if (!profileData.lastName.trim()) {
      toast.error('Last name is required.');
      return;
    }
    if (!profileData.email.trim()) {
      toast.error('Email is required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email.trim())) {
      toast.error('Please enter a valid email address.');
      return;
    }
    try {
      await authService.updateProfile(profileData);
      await refreshUser();
      toast.success('Profile updated successfully!');
      setIsEditing(false);
    } catch {
      toast.error('Failed to update profile.');
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters!');
      return;
    }

    if (hasPassword && passwordData.newPassword === passwordData.currentPassword) {
      toast.error('New password must be different from your current password.');
      return;
    }

    try {
      if (hasPassword) {
        await authService.changePassword({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        });
      } else {
        await authService.setPassword({
          newPassword: passwordData.newPassword,
        });
      }
      toast.success(hasPassword ? 'Password changed successfully!' : 'Password set successfully!');
      setIsChangingPassword(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      await refreshUser();
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || (hasPassword ? 'Failed to change password. Check your current password.' : 'Failed to set password.'));
    }
  };

  return (
    <div className="max-w-[800px] mx-auto">
      <div className="bg-white border border-zinc-200 rounded-card shadow-soft overflow-hidden">
        <div className="bg-gradient-to-br from-brand-500 to-violet-600 p-8 flex items-center gap-6">
          <div className="relative group">
            <div className="w-20 h-20 rounded-full bg-white text-brand-500 flex items-center justify-center text-2xl font-bold overflow-hidden">
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt="Profile avatar" className="w-full h-full object-cover" />
              ) : (
                <>{user?.firstName?.[0]}{user?.lastName?.[0]}</>
              )}
            </div>
            <button
              className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity text-white"
              onClick={() => setShowAvatarModal(true)}
              aria-label="Change profile photo"
            >
              <Camera className="w-6 h-6" />
            </button>
          </div>
          <div>
            <h1 className="text-white m-0 text-2xl font-display font-semibold">{user?.firstName} {user?.lastName}</h1>
            <p className="text-white/80 m-1 text-sm">@{user?.username}</p>
            {subLoading ? (
              <span className="inline-block px-3 py-1 bg-white/20 text-white rounded-full text-sm mt-2 font-medium">
                ...
              </span>
            ) : isPro ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-400 text-amber-950 rounded-full text-sm mt-2 font-semibold">
                <Crown className="w-3.5 h-3.5" /> Pro
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 text-white rounded-full text-sm mt-2 font-medium">
                <User className="w-3.5 h-3.5" /> Free
              </span>
            )}
          </div>
        </div>

        <div className="p-8 border-b border-zinc-100">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-foreground m-0 text-xl font-display font-semibold">Personal Information</h2>
            <button
              className="h-9 px-4 text-sm font-medium text-brand-500 bg-brand-50 hover:bg-brand-100 rounded-btn transition-colors inline-flex items-center gap-2"
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? <><X className="w-4 h-4" /> Cancel</> : <><Edit2 className="w-4 h-4" /> Edit</>}
            </button>
          </div>

          {isEditing ? (
            <form onSubmit={handleProfileUpdate}>
              <div className="flex gap-4 mb-6">
                <div className="flex-1">
                  <label className="block mb-2 text-sm text-secondary-foreground font-medium">First Name</label>
                  <input
                    type="text"
                    value={profileData.firstName}
                    onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                    className="w-full h-11 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                  />
                </div>
                <div className="flex-1">
                  <label className="block mb-2 text-sm text-secondary-foreground font-medium">Last Name</label>
                  <input
                    type="text"
                    value={profileData.lastName}
                    onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                    className="w-full h-11 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                  />
                </div>
              </div>
              <div className="mb-6">
                <label className="block mb-2 text-sm text-secondary-foreground font-medium">Email</label>
                <input
                  type="email"
                  value={profileData.email}
                  readOnly
                  className="w-full h-11 px-3 text-sm border border-zinc-200 rounded-btn bg-zinc-50 text-zinc-500 transition-all focus:outline-none cursor-not-allowed"
                />
                <p className="mt-1.5 text-xs text-zinc-400">Email address cannot be changed.</p>
              </div>
              <button type="submit" className="h-10 px-5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-btn transition-all duration-200 inline-flex items-center gap-2">
                <Save className="w-4 h-4" /> Save Changes
              </button>
            </form>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-6">
              <div>
                <label className="block text-sm text-secondary-foreground mb-1">Email</label>
                <p className="text-foreground text-base m-0">{user?.email}</p>
              </div>
              <div>
                <label className="block text-sm text-secondary-foreground mb-1">Username</label>
                <p className="text-foreground text-base m-0">{user?.username}</p>
              </div>
              <div>
                <label className="block text-sm text-secondary-foreground mb-1">Status</label>
                <p className="text-foreground text-base m-0">{user?.enabled ? 'Active' : 'Inactive'}</p>
              </div>
            </div>
          )}
        </div>

        <div className="p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-foreground m-0 text-xl font-display font-semibold">Security</h2>
          </div>

          {isChangingPassword ? (
            <form onSubmit={handlePasswordChange}>
              {hasPassword && (
                <div className="mb-6">
                  <label className="block mb-2 text-sm text-secondary-foreground font-medium">Current Password</label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    className="w-full h-11 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                    required
                  />
                </div>
              )}
              <div className="mb-6">
                <label className="block mb-2 text-sm text-secondary-foreground font-medium">New Password</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="w-full h-11 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                  required
                  minLength={6}
                />
              </div>
              <div className="mb-6">
                <label className="block mb-2 text-sm text-secondary-foreground font-medium">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="w-full h-11 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                  required
                />
              </div>
              <div className="flex gap-4">
                <button type="submit" className="h-10 px-5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-btn transition-all duration-200 inline-flex items-center gap-2">
                  <KeyRound className="w-4 h-4" /> {hasPassword ? 'Change Password' : 'Set Password'}
                </button>
                <button
                  type="button"
                  className="h-10 px-5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:text-foreground text-sm font-medium rounded-btn transition-colors inline-flex items-center gap-2"
                  onClick={() => setIsChangingPassword(false)}
                >
                  <X className="w-4 h-4" /> Cancel
                </button>
              </div>
            </form>
          ) : (
            <button
              className="h-10 px-5 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:text-foreground text-sm font-medium rounded-btn transition-colors inline-flex items-center gap-2"
              onClick={() => setIsChangingPassword(true)}
            >
              {hasPassword ? <Lock className="w-4 h-4" /> : <KeyRound className="w-4 h-4" />}
              {hasPassword ? 'Change Password' : 'Set Password'}
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-card shadow-soft overflow-hidden mt-6">
        <div className="p-8">
          <div className="flex items-center gap-3 mb-2">
            <Gift className="w-6 h-6 text-brand-500" />
            <h2 className="text-foreground m-0 text-xl font-display font-semibold">Refer & earn</h2>
          </div>
          <p className="text-secondary-foreground text-sm mt-1 mb-6">
            Earn {referral?.grantMonths ?? 12} months of Pro free when {referral?.threshold ?? 5} friends sign up
            through your link — or a single friend who goes paid.
          </p>

          {!referralLoading && referral && !referral.isReferred && (
            <div className="mb-6 p-4 border border-dashed border-brand-200 bg-brand-50/40 rounded-btn">
              <h3 className="text-sm font-semibold text-foreground mb-1">Got a referral code?</h3>
              <p className="text-xs text-secondary-foreground mb-3">
                If you signed up through a friend's link but the code was lost, you can enter it here.
              </p>
              {showClaimForm ? (
                <form onSubmit={handleClaimReferral} className="flex flex-col sm:flex-row gap-3">
                  <input
                    value={claimCode}
                    onChange={(e) => setClaimCode(e.target.value)}
                    placeholder="Enter referral code"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    disabled={claiming}
                    className="flex-1 h-11 px-3 text-sm border border-zinc-200 rounded-btn bg-white text-foreground focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none disabled:opacity-60"
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={claiming}
                      className="h-11 px-5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white text-sm font-medium rounded-btn transition-colors inline-flex items-center gap-2"
                    >
                      {claiming ? 'Applying...' : 'Apply'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowClaimForm(false); setClaimCode(''); }}
                      className="h-11 px-4 border border-zinc-200 text-zinc-600 hover:bg-zinc-50 text-sm font-medium rounded-btn transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  onClick={() => setShowClaimForm(true)}
                  className="h-10 px-5 border border-brand-200 text-brand-600 bg-white hover:bg-brand-50 text-sm font-medium rounded-btn transition-colors inline-flex items-center gap-2"
                >
                  <UserPlus className="w-4 h-4" /> Enter referral code
                </button>
              )}
            </div>
          )}

          {referralLoading ? (
            <div className="space-y-4">
              <div className="h-10 bg-zinc-100 animate-pulse rounded-btn" />
              <div className="h-4 bg-zinc-100 animate-pulse rounded w-1/2" />
            </div>
          ) : referral ? (
            <>
              {referral.isProGranted ? (
                <div className="flex items-center gap-2 bg-amber-50 text-amber-800 border border-amber-200 rounded-btn px-4 py-3 mb-6 text-sm font-medium">
                  <Crown className="w-4 h-4" /> Pro unlocked — you have {referral.grantMonths} months of free Pro.
                </div>
              ) : isDone ? (
                <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-btn px-4 py-3 mb-6 text-sm font-medium">
                  <UserCheck className="w-4 h-4" /> Goal reached — refresh to see your Pro badge.
                </div>
              ) : null}

              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <input
                  readOnly
                  value={referralLink ?? ''}
                  className="flex-1 h-11 px-3 text-sm border border-zinc-200 rounded-btn bg-zinc-50 text-foreground focus:outline-none"
                />
                <button
                  onClick={handleCopy}
                  className="h-11 px-5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-btn transition-colors inline-flex items-center gap-2"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy link'}
                </button>
              </div>

              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-secondary-foreground">Progress</span>
                  <span className="font-medium text-foreground">
                    {referral.verifiedCount}/{referral.threshold} verified
                    {referral.paidCount > 0 ? ` · ${referral.paidCount} paid` : ''}
                  </span>
                </div>
                <div className="h-2.5 bg-zinc-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${isDone ? 'bg-amber-400' : 'bg-brand-500'}`}
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>

              {referral.referrals.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-3">Referred users</h3>
                  <ul className="divide-y divide-zinc-100 border border-zinc-100 rounded-btn">
                    {referral.referrals.map((r) => (
                      <li key={r.userId} className="flex items-center justify-between px-4 py-3">
                        <span className="text-sm text-foreground truncate pr-4">{r.email || r.userId}</span>
                        {statusChip(r.status)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <p className="text-secondary-foreground text-sm">Unable to load your referral info.</p>
          )}
        </div>
      </div>
      <AvatarUploader
        open={showAvatarModal}
        onClose={() => setShowAvatarModal(false)}
        currentUrl={user?.avatarUrl}
        onUploaded={refreshUser}
      />
    </div>
  );
};

export default Profile;
